/**
 * Workspaces read from the signed-in user's B2C token, rather than from `GET /tenants/mine`.
 *
 * ── Why the token and not the API ──────────────────────────────────────────────────────────────
 *
 * CoordioCore builds its workspace list this way (`libs/auth/src/b2c-token-claims.ts`), and the two
 * products must agree on which workspace is selected. While this app derived its list from its own
 * database and Core derived its list from the claim, the same workspace carried two different
 * identities and neither app could honour the other's selection.
 *
 * They are reconcilable because the claim's middle field IS this app's tenant id — see
 * `OrganizationClaimParser` in `AgentOps.Application/Common/Helpers`, whose remark records the
 * mapping: *"parts[1] is the Cortex tenant id and parts[2] is the organization id"*. Core reads the
 * same `parts[1]` as its tenant id. One field, one meaning, two products.
 *
 * ── Claim format ──────────────────────────────────────────────────────────────────────────────
 *
 *   extension_Organizations = "Name~tenantId~orgId,Other Name~tenantId~orgId"
 *
 * Both ids must be GUIDs. That is not cosmetic validation: `TenantContextMiddleware` reads the
 * `X-Tenant-Id` header through `Guid.TryParse` and silently sets NO tenant context when it fails,
 * so a malformed entry would not raise an error — it would produce a workspace that quietly
 * returns another workspace's data shape with nothing in it. Dropping it here is the honest
 * outcome, and mirrors what the backend parser already does with the same string.
 */
import type { AccountInfo } from '@azure/msal-browser';

import { msalInstance } from '../config/msalInstance';
import type { TenantDto } from '../types/tenant';

/** One membership entry from `extension_Organizations`. */
export interface B2COrganization {
  /** Display name of the organization. */
  name: string;
  /** `parts[1]` — this app's tenant id, and the value sent as `X-Tenant-Id`. */
  tenantId: string;
  /** `parts[2]` — the directory's organization id. Carried for parity; not used for scoping. */
  orgId: string;
}

/**
 * Upper bound on memberships honoured from a single claim, matching `OrganizationClaimParser`.
 * A token is attacker-influenced input in the general case; an unbounded list would let one
 * oversized claim build an unbounded dropdown.
 */
export const MAX_ORGANIZATIONS = 50;

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

function isUsableGuid(value: string): boolean {
  return GUID_PATTERN.test(value) && value !== EMPTY_GUID;
}

/**
 * Finds the organizations claim, whichever name B2C used for it.
 *
 * B2C surfaces a custom attribute as `extension_Organizations` on most flows, but can prefix it
 * with the extensions-app id (`extension_<appId>_Organizations`). The backend's
 * `CurrentUserService.GetOrganizationsFromToken` already tolerates all three spellings; this
 * mirrors it, because a claim the API can read but the UI cannot would show an empty switcher on
 * an account that works perfectly server-side.
 */
export function readOrganizationsClaim(claims: Record<string, unknown> | undefined): string | undefined {
  if (!claims) return undefined;

  const direct = claims['extension_Organizations'];
  if (typeof direct === 'string' && direct.trim()) return direct;

  for (const [key, value] of Object.entries(claims)) {
    const isOrganizationsClaim =
      key.toLowerCase().endsWith('_organizations') || key.toLowerCase() === 'organizations';
    if (isOrganizationsClaim && typeof value === 'string' && value.trim()) {
      return value;
    }
  }

  return undefined;
}

/** Parses the raw claim value. Malformed entries are dropped rather than failing the whole list. */
export function parseOrganizationsClaim(raw: string | undefined): B2COrganization[] {
  if (!raw?.trim()) return [];

  const organizations: B2COrganization[] = [];

  for (const entry of raw.split(',')) {
    if (organizations.length >= MAX_ORGANIZATIONS) break;

    const parts = entry.trim().split('~').map((part) => part.trim());
    const [name, tenantId, orgId] = parts;

    if (!name || !tenantId || !orgId) continue;
    if (!isUsableGuid(tenantId) || !isUsableGuid(orgId)) continue;

    organizations.push({ name, tenantId, orgId });
  }

  return organizations;
}

/**
 * Converts memberships into the `TenantDto` shape the rest of the app already consumes.
 *
 * The claim carries a name and two ids and nothing else, so the remaining fields take defaults.
 * `isActive: true` is the meaningful one: a directory only lists memberships that exist, and the
 * switcher labels anything false as "(Inactive)" — defaulting to false would mark every real
 * workspace inactive. `projectCount` stays 0 because it is a database figure this list cannot
 * know; the pages that display it fetch tenants through the admin endpoints, not from here.
 */
export function organizationsToTenants(organizations: readonly B2COrganization[]): TenantDto[] {
  const seen = new Set<string>();
  const tenants: TenantDto[] = [];

  for (const organization of organizations) {
    const key = organization.tenantId.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    tenants.push({
      id: organization.tenantId,
      name: organization.name,
      slug: '',
      isActive: true,
      projectCount: 0,
    });
  }

  return tenants;
}

/**
 * The account MSAL considers signed in.
 *
 * Falls back to the first cached account because `setActiveAccount` is only guaranteed to have run
 * once a redirect has been processed in this document; a reload restores the cache without it.
 */
function resolveAccount(): AccountInfo | null {
  const active = msalInstance.getActiveAccount();
  if (active) return active;

  const accounts = msalInstance.getAllAccounts();
  return accounts.length > 0 ? accounts[0] : null;
}

/**
 * The workspaces the signed-in user belongs to, per their token.
 *
 * Returns an empty list when nobody is signed in or the claim is absent. The caller distinguishes
 * those cases — an absent claim on a signed-in account is a directory configuration problem, not
 * an empty membership list, and reporting it as "you belong to no workspaces" would send someone
 * looking in the wrong place.
 */
export function getTenantsFromToken(): TenantDto[] {
  const account = resolveAccount();
  if (!account) return [];

  const raw = readOrganizationsClaim(account.idTokenClaims as Record<string, unknown> | undefined);
  return organizationsToTenants(parseOrganizationsClaim(raw));
}

/** True when an account is signed in but its token carries no organizations claim at all. */
export function isOrganizationsClaimMissing(): boolean {
  const account = resolveAccount();
  if (!account) return false;

  return readOrganizationsClaim(account.idTokenClaims as Record<string, unknown> | undefined) === undefined;
}
